/**
 * @license Copyright (c) 2003-2021, CKSource - Frederico Knabben. All rights reserved.
 */

// eslint-disable-next-line no-redeclare
/* global console */

import {debounce} from 'lodash-es';
import {HighlightOption} from "@/enum/highlight_option";
import {EventBus} from "@/utils/EventBus";
import {getKeywordRegExpString, stripMarkers} from "@/utils/string";
import {captureException} from "@/plugins/Sentry";
import {Store} from "@/plugins/Store";
import {Environment} from "@/utils/Environment";

// delay between new editor value emit
const INPUT_EVENT_DEBOUNCE_WAIT = 50;
// delay between highlight application
const HIGHLIGHT_DEBOUNCE_WAIT = 50;

export default {
    name: 'ckeditor',

    render(h) {
        return h(this.tagName);
    },

    model: {
        prop: 'value',
        event: 'input'
    },

    props: {
        editor: {
            type: Function,
            default: null
        },
        value: {
            type: String,
            default: ''
        },
        config: {
            type: Object,
            default: () => ({})
        },
        tagName: {
            type: String,
            default: 'div'
        },
        wordsToHighlight: {
            type: Object, /* <{[key: string]: Array<string> }> */
            default: () => ({})
        },
        hideToolbar: {
            type: Boolean,
            default: false
        },
        multiline: {
            type: Boolean,
            default: true
        }
    },

    data: () => ({
        $_instance: null,
        $_lastEditorData: {
            type: String,
            default: ''
        },
        markers: [],
        debouncedInputEvent: null
    }),

    mounted() {
        const editorConfig = Object.assign({}, this.config);

        if (this.value) {
            editorConfig.initialData = this.value;
        }

        this.editor.create(this.$el, editorConfig)
            .then(editor => {
                this.$_instance = editor;

                if (this.hideToolbar) {
                    editor.ui.view.toolbar.element.parentElement.style.display = 'none';
                }

                this.$_setUpEditorEvents();

                this.$emit('ready', editor);

                // Set up global highlight colors for marker groups
                for (const markerClass of Object.values(HighlightOption)) {
                    editor.conversion.for('downcast').markerToHighlight({
                        model: `markerHighlight:${markerClass}`,
                        view: {
                            classes: markerClass
                        }
                    });
                }

                if (this.wordsToHighlight && Object.keys(this.wordsToHighlight).length) {
                    this.highlightWords();
                }

            })
            .catch(error => {
                Store.dispatch('displayFlashMessage', {
                    type: 'error',
                    content: 'unknownError'
                });
                captureException(error);
            });
    },

    beforeUnmount() {
        if (this.$_instance) {
            this.$_instance.destroy();
            this.$_instance = null;
        }

        this.$emit('destroy', this.$_instance);
    },

    created: function () {
        EventBus.$on('debounce-flush', () => {
            this.debouncedInputEvent();
            this.debouncedInputEvent.flush();
        });
    },

    computed: {
        debouncedWordHighlight: function () {
            return debounce(() => {
                this.highlightWords();
            }, HIGHLIGHT_DEBOUNCE_WAIT, {leading: true});
        }
    },

    watch: {
        value(newValue, oldValue) {
            if (newValue !== oldValue && newValue !== this.$_lastEditorData) {
                this.$_instance.setData(newValue);
            }
        },

        wordsToHighlight: {
            immediate: true,
            handler: function () {
                if (this.$_instance) {
                    this.debouncedWordHighlight();
                }
            }
        }
    },

    methods: {
        $_setUpEditorEvents() {
            const editor = this.$_instance;

            this.debouncedInputEvent = debounce(() => {
                const content = stripMarkers(editor.getData());
                if (this.$_lastEditorData !== content) {
                    this.$_lastEditorData = content;
                    this.debouncedWordHighlight();
                    this.$emit('input', content);
                }
            }, INPUT_EVENT_DEBOUNCE_WAIT, {leading: true});

            editor.model.document.on('change:data', this.debouncedInputEvent);

            editor.editing.view.document.on('focus', evt => {
                this.$emit('focus', evt, editor);
            });

            editor.editing.view.document.on('blur', evt => {
                this.$emit('blur', evt, editor);
            });

            if (!this.multiline) {
                // Prevent Enter key from creating new lines
                editor.editing.view.document.on('keydown', (evt, data) => {
                    if (data.domEvent.key === 'Enter') {
                        evt.stop();
                        data.preventDefault();
                    }
                });

                // Prevent pasting new lines
                editor.editing.view.document.on('paste', (evt, data) => {
                    const pastedData = data.dataTransfer.getData('text/plain');
                    const modifiedData = pastedData.replace(/\n/g, ' ');

                    evt.stop();
                    data.preventDefault();

                    editor.model.change(writer => {
                        // Getting correct position or selection for pasting
                        const selection = editor.model.document.selection;
                        const range = selection.getFirstRange();
            
                        // Delete the selected text
                        writer.remove(range);
            
                        // Insert the modified text
                        writer.insertText(modifiedData, range.start);
                      });
                });
            }
        },
        highlightWords: function () {

            const editor = this.$_instance;

            editor.model.change(writer => {

                const highlightOptions = Object.values(HighlightOption);
                // Clear all previously set markers
                // TODO try to optimize this and only clear those that have changed
                this.markers.forEach(markerName => writer.removeMarker(markerName));
                this.markers = [];
                let markerId = 0;
                let i = 0;

                for (const keywordPermutations of Object.values(this.wordsToHighlight)) {
                    const markerClass = highlightOptions[i++ % highlightOptions.length];
                    for (let keyword of keywordPermutations) {
                        keyword = keyword.toLowerCase();
                        const range = writer.createRangeIn(editor.model.document.getRoot());
                        for (const node of range.getWalker()) {
                            if (node.type !== 'text') {
                                continue;
                            }
                            let source = node.item.data.toLowerCase();
                            let skipped = 0;
                            while (source) {
                                let start = 0;
                                if (Environment.supportsPositiveLookBehindInRegexp) {
                                    start = source.search(getKeywordRegExpString(keyword));
                                    if (start === -1) {
                                        break;
                                    }
                                } else {
                                    const match = source.match(getKeywordRegExpString(keyword));
                                    if (match === null) {
                                        break;
                                    }
                                    const prefix = match[0].split(match[1])[0];
                                    start = (match.index + prefix.length);
                                }
                                const rootEl = node.previousPosition.root;
                                const elOffset = node.previousPosition.path[0];
                                const positionOffset = node.previousPosition.path[1] + skipped + start;
                                this.markers.push(writer.addMarker(`markerHighlight:${markerClass}:${markerId++}`, {
                                    range: writer.createRange(
                                        writer.createPositionFromPath(rootEl, [elOffset, positionOffset]),
                                        writer.createPositionFromPath(rootEl, [elOffset, positionOffset + keyword.length])
                                    ),
                                    usingOperation: false,
                                    affectsData: false
                                }));
                                skipped += start + keyword.length;
                                source = source.slice(start + keyword.length);
                            }
                        }
                    }
                }
            });
        }
    }
};
